import React, { useState, useContext } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Clock, Briefcase, Building2, IndianRupee, Lightbulb } from 'lucide-react';
import Card3D from '../components/Card3D';
import NavigationExact from '../components/NavigationExact';
import FooterExact from '../components/FooterExact';
import { ApplyContext } from '../context/ApplyContext';

const CareersPage = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const { openApplyModal } = useContext(ApplyContext);

  const benefits = [
    {
      icon: Building2,
      title: 'Growth Opportunities',
      desc: 'Work directly on AI underwriting, trade data pipelines, and GSTN integrations. You will not spend years waiting for responsibility — you will own it from week one.'
    },
    {
      icon: IndianRupee,
      title: 'Competitive Benefits',
      desc: 'Competitive compensation, health coverage for you and your family, and performance bonuses tied to platform outcomes — not just individual targets.'
    },
    {
      icon: Lightbulb,
      title: 'Innovative Culture',
      desc: 'We move fast but we build right. Every credit decision on our platform is backed by real trade data — you will build systems that actually matter to the 1,200+ businesses depending on them.'
    }
  ];

  const departments = ['All', 'Financial Planning & Analysis', 'Technology & Product Development', 'Sales & Business Development'];

  const jobs = [
    {
      id: 1,
      title: 'Financial Analyst',
      department: 'Financial Planning & Analysis',
      location: 'Mumbai, India',
      type: 'Full-time',
      description: "We're building the credit intelligence layer behind India's supply chain finance market. As our Financial Analyst, you will own invoice portfolio analytics, anchor program performance reporting, and PAR monitoring across our ₹3,000+ M book. Strong financial modelling and understanding of trade credit is essential."
    },
    {
      id: 2,
      title: 'Senior Software Engineer (Fintech)',
      department: 'Technology & Product Development',
      location: 'Mumbai, India',
      type: 'Full-time',
      description: 'You will build the core infrastructure that processes GSTN data, e-invoice verification, and Account Aggregator signals in real time. Our underwriting engine runs on verified trade data — you will design the systems that make that possible at scale. Backend engineering and cloud infrastructure experience required.'
    },
    {
      id: 3,
      title: 'Relationship Manager — SCF Origination',
      department: 'Sales & Business Development',
      location: 'Mumbai, India',
      type: 'Full-time',
      description: 'You will be the face of Funding Sathi to anchor corporates and their supplier networks across Mumbai. Your job is not to sell loans — it is to onboard anchors, map their supply chains, and bring verified trade onto our platform. Experience in trade finance, SCF, or MSME banking preferred.'
    }
  ];

  const filteredJobs = activeFilter === 'All' ? jobs : jobs.filter(job => job.department === activeFilter);

  return (
    <>
      <NavigationExact />
      <div className="pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-red-50/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.span 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block px-4 py-1.5 bg-red-100 text-[#5C1F33] rounded-full text-sm font-medium mb-6"
          >
            ● Join Our Team
          </motion.span>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
          >
            Build Your <span className="text-[#5C1F33]">Career</span>
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-gray-600 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed"
          >
            Join us in bridging India's ₹30 lakh crore MSME credit gap — one verified invoice at a time.
          </motion.p>

          <motion.a
            href="mailto:corporate@fundingsathi.in"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex w-full sm:w-auto sm:justify-center items-center gap-2 mt-8 bg-[#5C1F33] text-white px-6 py-3 rounded-lg hover:bg-[#800020] transition-colors"
          >
            Contact Us →
          </motion.a>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Work With Us?</h2>
            <p className="text-gray-500">Work on technology that has already unlocked ₹3,000+ M for Indian businesses. Here is what that looks like day to day.</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card3D className="h-full">
                  <div className="bg-white rounded-2xl p-8 border border-gray-100 hover:shadow-lg transition-all h-full">
                    <div className="w-12 h-12 bg-red-50 rounded-2xl border border-[#5C1F33]/20 flex items-center justify-center mb-6">
                      <benefit.icon className="w-6 h-6 text-[#5C1F33] stroke-[1.5]" />
                    </div>
                    <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{benefit.desc}</p>
                  </div>
                </Card3D>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">Open Positions</h2>
          
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-8">
            {departments.map((dept) => (
              <button
                key={dept}
                onClick={() => setActiveFilter(dept)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeFilter === dept
                    ? 'bg-[#5C1F33] text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {dept === 'All' ? 'All Departments' : dept}
              </button>
            ))}
          </div>

          {/* Job Listings */}
          <div className="space-y-4">
            {filteredJobs.length > 0 ? (
              filteredJobs.map((job, index) => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card3D>
                    <div className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-lg transition-all">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Briefcase className="w-5 h-5 text-[#5C1F33]" />
                            <h3 className="text-lg sm:text-xl font-bold">{job.title}</h3>
                          </div>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-4">
                            <span className="flex items-center gap-1">
                              <Building2 className="w-4 h-4" />
                              {job.department}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {job.location}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {job.type}
                            </span>
                          </div>
                          
                          <p className="text-gray-600 text-sm leading-relaxed">
                            {job.description}
                          </p>
                        </div>
                        
                        <button
                          type="button"
                          onClick={() => openApplyModal(job)}
                          className="px-6 py-3 rounded-lg bg-[#5C1F33] text-white font-medium hover:bg-[#4a1829] hover:-translate-y-0.5 transition-all flex-shrink-0 text-sm relative z-10 inline-flex items-center justify-center"
                        >
                          Apply Now
                        </button>
                      </div>
                    </div>
                  </Card3D>
                </motion.div>
              ))
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-16"
              >
                <div className="w-16 h-16 bg-red-50 rounded-2xl border border-[#5C1F33]/20 flex items-center justify-center mx-auto mb-4">
                  <Briefcase className="w-8 h-8 text-[#5C1F33]" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">No Open Positions</h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  We don't have any open positions in {activeFilter === 'All' ? 'this category' : activeFilter} right now. Check back soon or explore other departments!
                </p>
                <button 
                  onClick={() => setActiveFilter('All')}
                  className="mt-6 px-6 py-2 bg-[#5C1F33] text-white rounded-full text-sm font-medium hover:bg-[#4a1829] transition-colors"
                >
                  View All Positions
                </button>
              </motion.div>
            )}
          </div>
        </div>
      </section>
      </div>

      <FooterExact />
    </>
  );
};

export default CareersPage;
